import { useState } from "react";
import { toast } from "sonner";
import { api, mediaUrl } from "@/lib/api";
import { X, Upload, Loader2, Trash2, ArrowLeft, ArrowRight } from "lucide-react";

const ALL_SIZES = ["XS", "S", "M", "L", "XL", "XXL"];
import { CATEGORIES } from "@/lib/categories";

export const ProductForm = ({ initial, onClose, onSaved }) => {
  const editing = !!initial;
  const [form, setForm] = useState(
    initial || {
      name: "",
      price: "",
      description: "",
      category: "Jackets",
      size: "",
      sold: false,
      images: [],
      featured: false,
      published: true,
    }
  );
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => ({ ...f, images: [...f.images, data.path] }));
      }
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (idx) => setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  const moveImage = (idx, dir) =>
    setForm((f) => {
      const arr = [...f.images];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return f;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return { ...f, images: arr };
    });

  const makeMain = (idx) =>
    setForm((f) => {
      if (idx === 0) return f;
      const arr = [...f.images];
      const [img] = arr.splice(idx, 1);
      arr.unshift(img);
      return { ...f, images: arr };
    });

  const save = async () => {
    if (!form.name || form.price === "" || !form.size) {
      toast.error("Name, price and size are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        sold: !!form.sold,
      };
      if (editing) await api.put(`/admin/products/${initial.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing ? "Product updated" : "Product published");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const field = "w-full bg-transparent border border-white/20 px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-white/60 text-sm";
  const label = "font-mono-label text-[10px] uppercase tracking-luxe text-white/40 mb-2 block";

  return (
    <div className="fixed inset-0 z-[80] bg-black/80 flex items-start justify-center overflow-y-auto py-10 px-4">
      <div data-testid="product-form-modal" className="bg-[#0A0A0A] border border-white/10 w-full max-w-2xl">
        <div className="flex items-center justify-between px-8 py-6 border-b border-white/10 sticky top-0 bg-[#0A0A0A]">
          <h2 className="font-serif-display text-2xl font-light">{editing ? "Edit Product" : "New Product"}</h2>
          <button data-testid="product-form-close" onClick={onClose} className="text-white/60 hover:text-white">
            <X strokeWidth={1.3} />
          </button>
        </div>

        <div className="px-8 py-8 space-y-6">
          <div>
            <label className={label}>Product Name</label>
            <input data-testid="pf-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} placeholder="Sculptural Trench Coat" />
          </div>

          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className={label}>Price (CHF, Fr.)</label>
              <input data-testid="pf-price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={field} placeholder="Fr. 450.00" />
            </div>
            <div>
              <label className={label}>Category</label>
              <select data-testid="pf-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={field}>
                {!CATEGORIES.includes(form.category) && (
                  <option value={form.category} className="bg-black">{form.category || "—"} (old category — please choose)</option>
                )}
                {CATEGORIES.map((c) => (
                  <option key={c} value={c} className="bg-black">{c}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className={label}>Description</label>
            <textarea data-testid="pf-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={field} />
          </div>

          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className={label}>Size</label>
              <select data-testid="pf-size" value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} className={field}>
                <option value="" className="bg-black">Select size</option>
                {ALL_SIZES.map((s) => (
                  <option key={s} value={s} className="bg-black">{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={label}>Availability</label>
              <div className="flex border border-white/20">
                <button
                  type="button"
                  data-testid="pf-available"
                  onClick={() => setForm({ ...form, sold: false })}
                  className={`flex-1 py-3 font-mono-label text-xs uppercase tracking-luxe transition-colors ${!form.sold ? "bg-white text-black" : "text-white/60 hover:text-white"}`}
                >
                  Available
                </button>
                <button
                  type="button"
                  data-testid="pf-sold"
                  onClick={() => setForm({ ...form, sold: true })}
                  className={`flex-1 py-3 font-mono-label text-xs uppercase tracking-luxe transition-colors border-l border-white/20 ${form.sold ? "bg-white text-black" : "text-white/60 hover:text-white"}`}
                >
                  Sold
                </button>
              </div>
            </div>
          </div>

          <div>
            <label className={label}>Photos <span className="text-white/30 normal-case tracking-normal">— first image is the main image</span></label>
            <div className="flex flex-wrap gap-3 mb-3">
              {form.images.map((img, i) => (
                <div key={i} className="relative w-24 h-28 bg-neutral-900 overflow-hidden group border border-white/10">
                  <img src={mediaUrl(img)} alt="" className="w-full h-full object-cover" />
                  {i === 0 && (
                    <span className="absolute top-1 left-1 bg-white text-black text-[8px] font-mono-label uppercase tracking-wider px-1.5 py-0.5">Main</span>
                  )}
                  <div className="absolute inset-0 bg-black/75 opacity-0 group-hover:opacity-100 flex flex-col items-center justify-center gap-1 text-white transition-opacity">
                    <div className="flex gap-2">
                      <button type="button" onClick={() => moveImage(i, -1)} data-testid={`pf-move-left-${i}`} className="text-white/80 hover:text-white disabled:opacity-30" disabled={i === 0}>
                        <ArrowLeft size={14} />
                      </button>
                      <button type="button" onClick={() => moveImage(i, 1)} data-testid={`pf-move-right-${i}`} className="text-white/80 hover:text-white disabled:opacity-30" disabled={i === form.images.length - 1}>
                        <ArrowRight size={14} />
                      </button>
                      <button type="button" onClick={() => removeImage(i)} data-testid={`pf-remove-image-${i}`} className="text-white/80 hover:text-red-400">
                        <Trash2 size={14} />
                      </button>
                    </div>
                    {i !== 0 && (
                      <button type="button" onClick={() => makeMain(i)} data-testid={`pf-make-main-${i}`} className="text-[8px] font-mono-label uppercase tracking-wider border border-white/40 px-1.5 py-0.5 hover:bg-white hover:text-black">
                        Set main
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <label className="w-20 h-24 border border-dashed border-white/25 flex items-center justify-center cursor-pointer hover:border-white/60 text-white/50">
                {uploading ? <Loader2 className="animate-spin" size={18} /> : <Upload size={18} strokeWidth={1.3} />}
                <input data-testid="pf-upload-input" type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" />
              </label>
            </div>
          </div>

          <div className="flex gap-8">
            <label className="flex items-center gap-3 cursor-pointer">
              <input data-testid="pf-featured" type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-white w-4 h-4" />
              <span className="font-mono-label text-xs uppercase tracking-luxe text-white/70">Featured (New Drop)</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input data-testid="pf-published" type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="accent-white w-4 h-4" />
              <span className="font-mono-label text-xs uppercase tracking-luxe text-white/70">Published</span>
            </label>
          </div>
        </div>

        <div className="px-8 py-6 border-t border-white/10 flex justify-end gap-3">
          <button onClick={onClose} className="px-8 py-3 font-mono-label text-xs uppercase tracking-luxe border border-white/25 text-white/70 hover:text-white">
            Cancel
          </button>
          <button data-testid="pf-save" onClick={save} disabled={saving} className="px-8 py-3 bg-white text-black font-mono-label text-xs uppercase tracking-luxe hover-invert hover:bg-transparent hover:text-white border border-white disabled:opacity-50">
            {saving ? "Saving…" : editing ? "Update" : "Publish"}
          </button>
        </div>
      </div>
    </div>
  );
};
